/**
 * Authentication context provider using AWS Amplify
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react'
import {
  getCurrentUser,
  signOut as amplifySignOut,
  signIn as amplifySignIn,
  signInWithRedirect,
  fetchAuthSession,
  confirmSignIn,
  AuthUser,
} from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'

// Sign-in result types
export interface SignInSuccess {
  status: 'success'
}

export interface SignInNeedsNewPassword {
  status: 'newPasswordRequired'
}

export interface SignInError {
  status: 'error'
  message: string
}

export type SignInResult = SignInSuccess | SignInNeedsNewPassword | SignInError

interface AuthContextType {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  needsNewPassword: boolean
  signIn: () => Promise<void>
  signInWithCredentials: (email: string, password: string) => Promise<SignInResult>
  completeNewPassword: (newPassword: string) => Promise<SignInResult>
  signOut: () => Promise<void>
  getAccessToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [needsNewPassword, setNeedsNewPassword] = useState(false)

  useEffect(() => {
    // Check current auth state on mount
    checkAuth()

    // Listen for auth events
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          checkAuth()
          break
        case 'signedOut':
          setUser(null)
          break
        case 'tokenRefresh':
          checkAuth()
          break
      }
    })

    return () => unsubscribe()
  }, [])

  async function checkAuth() {
    try {
      const currentUser = await getCurrentUser()
      setUser(currentUser)
    } catch {
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  async function signIn() {
    await signInWithRedirect()
  }

  async function signInWithCredentials(email: string, password: string): Promise<SignInResult> {
    try {
      const result = await amplifySignIn({
        username: email,
        password,
      })

      if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNeedsNewPassword(true)
        return { status: 'newPasswordRequired' }
      }

      if (result.isSignedIn) {
        await checkAuth()
        return { status: 'success' }
      }

      // Handle other steps if needed
      return { status: 'error', message: `Unexpected step: ${result.nextStep.signInStep}` }
    } catch (err) {
      const error = err as Error
      // Map Cognito error codes to user-friendly messages
      if (error.name === 'NotAuthorizedException') {
        return { status: 'error', message: 'Incorrect email or password' }
      }
      if (error.name === 'UserNotFoundException') {
        return { status: 'error', message: 'No account found with this email' }
      }
      if (error.name === 'UserNotConfirmedException') {
        return { status: 'error', message: 'Please verify your email before signing in' }
      }
      return { status: 'error', message: error.message || 'Failed to sign in' }
    }
  }

  async function completeNewPassword(newPassword: string): Promise<SignInResult> {
    try {
      const result = await confirmSignIn({
        challengeResponse: newPassword,
      })

      if (result.isSignedIn) {
        setNeedsNewPassword(false)
        await checkAuth()
        return { status: 'success' }
      }

      return { status: 'error', message: 'Failed to set new password' }
    } catch (err) {
      const error = err as Error
      if (error.name === 'InvalidPasswordException') {
        return { status: 'error', message: 'Password does not meet requirements. Use at least 8 characters with uppercase, lowercase, and numbers.' }
      }
      return { status: 'error', message: error.message || 'Failed to set new password' }
    }
  }

  async function signOut() {
    await amplifySignOut()
    setUser(null)
    setNeedsNewPassword(false)
  }

  async function getAccessToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession()
      // Use idToken for API Gateway JWT authorizer (has 'aud' claim)
      return session.tokens?.idToken?.toString() || null
    } catch {
      return null
    }
  }

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    needsNewPassword,
    signIn,
    signInWithCredentials,
    completeNewPassword,
    signOut,
    getAccessToken,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
